import Backbone from 'backbone';
import _ from 'underscore';
import { Collection } from 'lib/backbone_extensions';
import ModelStore from 'stores/anyguide/model';

const cachedCollections: Record<string, any> = {};
const callbacks: Record<string, ((arg: any) => void)[]> = {};

interface RequireOptions {
  forceFetch?: boolean;
}

interface Filters {
  [key: string]: any;
}

const CollectionStore = {
  CACHE_EXPIRE: 100000,

  require(
    ancestorPath: (string | Backbone.Model)[],
    filters: Filters,
    opts: RequireOptions = {}
  ): any {
    ancestorPath = _.flatten([ancestorPath]);
    const collectionKey = this._buildKey(ancestorPath, filters);
    if (this._collectionCached(collectionKey)) {
      return this._loadFromCache(collectionKey, opts);
    }
    const collection = new (this._buildCollection(
      ancestorPath,
      filters,
      collectionKey
    ))();
    this._fetchCollection(collection);
    this._registerCallbacks(collection);
    this._cacheCollection(collection);
    return collection;
  },

  bind(collection: any, fn: (arg: any) => void): void {
    callbacks[collection.cid] = callbacks[collection.cid] || [];
    callbacks[collection.cid].push(fn);
    if (!collection._fetching) fn(collection);
  },

  unbind(collection: any, fn: (arg: any) => void): void {
    const index = _.indexOf(callbacks[collection.cid] || [], fn);
    if (index !== -1) callbacks[collection.cid].splice(index, 1);
  },

  _fetchCollection(collection: any): any {
    collection.fetch();
    collection._fetching = true;
    collection._lastFetchAt = new Date().getTime();
    return collection;
  },

  _registerCallbacks(collection: any): void {
    collection.on('sync', () => {
      collection._fetching = false;
      _.each(callbacks[collection.cid], (fn) => fn(collection));
    });
  },

  _cacheCollection(collection: any): void {
    cachedCollections[collection._key] = collection;
  },

  _refreshCollections(modelName: string): void {
    _.each(_.values(cachedCollections), (col: { modelName: string }) => {
      if (col.modelName === modelName && this._isUsed(col)) {
        this._fetchCollection(col);
      }
    });
  },

  _isUsed(collection: any): boolean {
    return (callbacks[collection.cid] || []).length > 0;
  },

  _loadFromCache(collectionKey: string, opts: RequireOptions): any {
    const collection = cachedCollections[collectionKey];
    if (opts.forceFetch || this._shouldFetch(collection)) {
      this._fetchCollection(collection);
    }
    return collection;
  },

  _collectionCached(collectionKey: string): boolean {
    return !!cachedCollections[collectionKey];
  },

  _shouldFetch(collection: any): boolean {
    return collection._lastFetchAt + this.CACHE_EXPIRE < Date.now();
  },

  _buildKey(
    ancestorPath: (string | Backbone.Model)[],
    filters: Filters
  ): string {
    let key = _.reduce(
      ancestorPath,
      (acc, ancestor) => {
        if (ancestor instanceof Backbone.Model) {
          // @ts-ignore
          acc += `${ancestor.urlSegment}/${ancestor.id}-`;
        } else if (typeof ancestor === 'string') {
          acc += ancestor;
        }
        return acc;
      },
      ''
    );

    const sortedFilterPairs = _.sortBy(
      _.pairs(filters),
      (pair: any[]) => pair[0]
    );
    _.each(sortedFilterPairs, (pair: any[]) => {
      key += `-${pair[0]}=${JSON.stringify(pair[1])}`;
    });

    return key;
  },

  _buildCollection(
    ancestorPath: (string | Backbone.Model)[],
    filters: Filters,
    collectionKey: string
  ): any {
    let urlBase = ModelStore.urlPrefix;
    let model: any;
    let modelName: string | undefined;

    _.each(ancestorPath, (ancestor) => {
      if (ancestor instanceof Backbone.Model) {
        // @ts-ignore
        urlBase += `/${ancestor.urlSegment}/${ancestor.id}`;
      } else {
        modelName = ancestor;
        model = ModelStore._registeredModels[ancestor];
        urlBase += `/${model.prototype.urlSegment}`;
      }
    });

    return Collection.extend({
      modelName,
      model,
      urlBase,
      filters,
      cid: `c${_.uniqueId()}`,
      _key: collectionKey,
    });
  },

  _clearCachedCollections(): void {
    _.each(cachedCollections, (col: { stopListening: () => any }) =>
      col.stopListening()
    );
    _.each(
      cachedCollections,
      (_: any, key: string | number) => delete cachedCollections[key]
    );
  },

  _callbacks: callbacks,
  _cachedCollections: cachedCollections,
};

export default CollectionStore;
